import {
  ConsultationRequestApi,
  type ConsultationRequestCreationDTO,
  type ConsultationRequestFilterDTO,
  type ConsultationRequestReportDTO,
  type GetConsultationRequestReportResponse,
  type GetConsultationRequestsWithCaseResponse,
  type GetPaginatedConsultationsRequestsResponse,
  type PaginatedResponseConsultationSummaryDTO,
  ScheduledConsultationRequestApi,
  type ScheduledConsultationCreationDTO,
  UrgencyDTO,
  type ScheduledConsultationDTO,
  type ScheduledConsultationReportDTO,
  type GetScheduledConsultationReportResponse,
  type ScheduledConsultationRequestReportDTO,
  type UpdateScheduledConsultationParticipantDTO,
  type CompletedConsultationPatientsResponse,
  type FilteringOptions
} from '@/generated'
import { globalApiConfig } from '@/services/telehealthApi/APIConfig'

export class ConsultationService {
  static get consultationApi() {
    return new ConsultationRequestApi(globalApiConfig)
  }

  static get scheduledConsultationApi() {
    return new ScheduledConsultationRequestApi(globalApiConfig)
  }

  static async getIncomingConsultationRequests(): Promise<GetPaginatedConsultationsRequestsResponse> {
    return (
      await ConsultationService.consultationApi.getIncomingConsultationRequests([
        UrgencyDTO.URGENT,
        UrgencyDTO.ROUTINE,
        UrgencyDTO.EMERGENCY
      ])
    ).data
  }

  static async getOutgoingConsultationRequests(
    page?: number,
    size?: number
  ): Promise<GetPaginatedConsultationsRequestsResponse> {
    return (await ConsultationService.consultationApi.getOutgoingConsultationRequests(page, size))
      .data
  }

  static async getCompletedConsultationRequests(
    filters: FilteringOptions,
    page?: number,
    size?: number,
    sort?: Array<string>,
    signal?: AbortSignal
  ): Promise<PaginatedResponseConsultationSummaryDTO> {
    return (
      await ConsultationService.consultationApi.findCompletedConsultationRequests(
        page,
        size,
        sort,
        filters,
        {
          signal
        }
      )
    ).data
  }

  static async createConsultationRequest(request: ConsultationRequestCreationDTO): Promise<string> {
    return (await ConsultationService.consultationApi.createConsultationRequest(request)).data
  }

  static async acceptConsultationRequest(id: string): Promise<void> {
    await ConsultationService.consultationApi.acceptConsultationRequest(id)
  }

  static async declineConsultationRequest(id: string): Promise<void> {
    await ConsultationService.consultationApi.declineConsultationRequest(id)
  }

  static async deleteConsultationRequest(id: string): Promise<void> {
    await ConsultationService.consultationApi.deleteConsultationRequest(id)
  }

  static async getConsultationRequestReport(
    id: string
  ): Promise<GetConsultationRequestReportResponse> {
    return (await ConsultationService.consultationApi.getConsultationRequestReport(id)).data
  }

  static async putConsultationRequestReport(
    id: string,
    report: ConsultationRequestReportDTO,
    finalize: boolean = false
  ): Promise<void> {
    await ConsultationService.consultationApi.putConsultationRequestReport(id, report, finalize)
  }

  static async createEmergencyRequest(caseId: string): Promise<string> {
    const intensiveCareLiteral = 'intensive_care_medicine'

    const submittedConsultationRequest: ConsultationRequestCreationDTO = {
      caseId,
      medicalFieldId: intensiveCareLiteral,
      question: '',
      diagnosis: '',
      urgency: UrgencyDTO.EMERGENCY,
      customFields: []
    }
    return await ConsultationService.createConsultationRequest(submittedConsultationRequest)
  }

  static async findConsultationRequestsByFilter(
    filterDTO: ConsultationRequestFilterDTO
  ): Promise<GetConsultationRequestsWithCaseResponse> {
    const filteredFilterDTO = Object.fromEntries(
      Object.entries(filterDTO).filter(([_, value]) => value !== null && value !== undefined)
    )

    return (
      await ConsultationService.consultationApi.findConsultationRequestsByFilter(filteredFilterDTO)
    ).data
  }

  static async checkDoctorAvailability(
    medicalFieldId: string,
    caseId: string,
    urgency: UrgencyDTO
  ): Promise<boolean> {
    const response = await ConsultationService.consultationApi.checkDoctorAvailability(
      medicalFieldId,
      caseId,
      urgency
    )
    return response.data
  }

  static async createScheduledConsultationRequest(
    request: ScheduledConsultationCreationDTO
  ): Promise<string> {
    const response =
      await ConsultationService.scheduledConsultationApi.createScheduledConsultation(request)

    return response.data
  }

  static async getScheduledConsultationRequest(
    scheduledConsultationId: string
  ): Promise<ScheduledConsultationDTO> {
    const response =
      await ConsultationService.scheduledConsultationApi.getScheduledConsultation(
        scheduledConsultationId
      )

    return response.data
  }

  static async deleteScheduledConsultationRequest(scheduledConsultationId: string): Promise<void> {
    await ConsultationService.scheduledConsultationApi.deleteScheduledConsultation(
      scheduledConsultationId
    )
  }

  static async acceptScheduledConsultationRequest(scheduledConsultationId: string): Promise<void> {
    await ConsultationService.scheduledConsultationApi.acceptScheduledConsultation(
      scheduledConsultationId
    )
  }

  static async declineScheduledConsultationRequest(scheduledConsultationId: string): Promise<void> {
    await ConsultationService.scheduledConsultationApi.declineScheduledConsultation(
      scheduledConsultationId
    )
  }

  static async hideScheduledConsultationRequest(scheduledConsultationId: string): Promise<void> {
    await ConsultationService.scheduledConsultationApi.hideScheduledConsultation(
      scheduledConsultationId
    )
  }
  static async completeScheduledConsultationRequest(
    scheduledConsultationId: string
  ): Promise<void> {
    await ConsultationService.scheduledConsultationApi.completeScheduledConsultation(
      scheduledConsultationId
    )
  }

  static async updateScheduledConsultationRequestCases(
    scheduledConsultationId: string,
    cases: string[]
  ): Promise<void> {
    await ConsultationService.scheduledConsultationApi.updateScheduledConsultationCases(
      scheduledConsultationId,
      { caseIds: cases }
    )
  }

  static async updateScheduledConsultationRequestRecipients(
    scheduledConsultationId: string,
    participants: UpdateScheduledConsultationParticipantDTO[],
    requesterShouldWriteReport: boolean = false
  ): Promise<void> {
    await ConsultationService.scheduledConsultationApi.updateScheduledConsultationRecipients(
      scheduledConsultationId,
      { participants, requesterShouldWriteReport: requesterShouldWriteReport }
    )
  }

  static async getScheduledConsultationReports(
    scheduledConsultationId: string
  ): Promise<GetScheduledConsultationReportResponse> {
    const response =
      await ConsultationService.scheduledConsultationApi.getScheduledConsultationReports(
        scheduledConsultationId
      )
    return response.data
  }

  static async getScheduledConsultationReport(
    scheduledConsultationId: string,
    caseId: string
  ): Promise<ScheduledConsultationReportDTO> {
    return (
      await ConsultationService.scheduledConsultationApi.getScheduledConsultationReport(
        scheduledConsultationId,
        caseId
      )
    ).data
  }

  static async putScheduledConsultationReport(
    scheduledConsultationId: string,
    caseId: string,
    scheduledConsultationRequestReport: ScheduledConsultationRequestReportDTO,
    shouldFinalizeScheduledConsultationReport: boolean = false
  ): Promise<void> {
    await ConsultationService.scheduledConsultationApi.putScheduledConsultationReport(
      scheduledConsultationId,
      caseId,
      scheduledConsultationRequestReport,
      shouldFinalizeScheduledConsultationReport
    )
  }

  static async getCompletedConsultationPatients(): Promise<CompletedConsultationPatientsResponse> {
    return (await ConsultationService.consultationApi.getCompletedConsultationPatients()).data
  }
}
